import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import ButtonV2 from 'components/ButtonV2';
import EnhancedThemeProvider from 'components/EnhancedThemeProvider';
import { DateSelect } from 'components/FormFields';
import { Modal } from 'components/Modal';
import useTheme from 'hooks/useTheme';

const ModalContent = styled.div`
  padding: 24px;
  background: ${({ theme }) =>
    theme?.name === 'dark' ? '#1e293b' : '#ffffff'};
`;

const ModalHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px;
  border-bottom: 1px solid
    ${({ theme }) => (theme?.name === 'dark' ? '#334155' : '#e2e8f0')};
  background: ${({ theme }) =>
    theme?.name === 'dark' ? '#1e293b' : '#ffffff'};
`;

const ModalHeaderTitle = styled.h2`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#ffffff' : '#0f172a')};
  margin: 0;
`;

const CloseButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: ${({ theme }) =>
    theme?.name === 'dark'
      ? 'rgba(255, 255, 255, 0.1)'
      : 'rgba(0, 0, 0, 0.05)'};
  color: ${({ theme }) => (theme?.name === 'dark' ? '#94a3b8' : '#64748b')};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;

  &:hover {
    background: ${({ theme }) =>
      theme?.name === 'dark'
        ? 'rgba(255, 255, 255, 0.15)'
        : 'rgba(0, 0, 0, 0.08)'};
    color: ${({ theme }) => (theme?.name === 'dark' ? '#ffffff' : '#0f172a')};
  }
`;

const CloseIcon = () => (
  <svg
    width="18"
    height="18"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
  >
    <path d="M18 6L6 18M6 6l12 12" />
  </svg>
);

const ModalDescription = styled.p`
  font-size: 14px;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#94a3b8' : '#64748b')};
  margin: 0 0 24px 0;
  line-height: 1.5;
  text-align: center;
`;

const TemplateName = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: ${({ theme }) => (theme?.name === 'dark' ? '#ffffff' : '#0f172a')};
  margin-bottom: 4px;
  text-align: center;
`;

const TemplatePrice = styled.div`
  font-size: 16px;
  color: #10b981;
  font-weight: 700;
  margin-bottom: 20px;
  text-align: center;
`;

const FormRowGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  margin-bottom: 20px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid
    ${({ theme }) => (theme?.name === 'dark' ? '#334155' : '#e2e8f0')};
`;

const ButtonWrapper = styled.div`
  width: auto;

  button {
    width: auto;
    padding: 10px 24px;
  }
`;

const ErrorText = styled.div`
  color: #ef4444;
  font-size: 13px;
  margin-top: 8px;
  text-align: center;
`;

// Format price for display
const formatPrice = (price) => {
  const num = Number(price) || 0;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(num);
};

// Parse MM-DD-YYYY to Date object
const parseDate = (dateStr) => {
  if (!dateStr) return null;
  const [month, day, year] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Get year from MM-DD-YYYY string
const getYearFromDate = (dateStr) => {
  if (!dateStr) return new Date().getFullYear();
  const parts = dateStr.split('-');
  return parts.length === 3 ? Number(parts[2]) : new Date().getFullYear();
};

// Inner content component that uses theme
const ModalContentInner = ({
  template,
  event,
  isSubmitting,
  onClose,
  onSubmit,
  isVisible,
}) => {
  const theme = useTheme();
  const isDark = theme?.name === 'dark';

  // Initialize dates from event's date range
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [error, setError] = useState('');

  // Reset dates when modal opens with new template
  useEffect(() => {
    if (isVisible && event) {
      // Default to event's start date for ticket start
      setStartDate(event.start_date || '');
      setEndDate(event.end_date || '');
      setError('');
    }
  }, [isVisible, event]);

  const handleSubmit = () => {
    // Validate dates
    if (!startDate) {
      setError('Please select a start date');
      return;
    }
    if (!endDate) {
      setError('Please select an end date');
      return;
    }

    const start = parseDate(startDate);
    const end = parseDate(endDate);

    if (end < start) {
      setError('End date must be after start date');
      return;
    }

    // Validate against event dates
    if (event) {
      const eventStart = parseDate(event.start_date);
      const eventEnd = parseDate(event.end_date);

      if (start < eventStart || start > eventEnd) {
        setError('Start date must be within the event date range');
        return;
      }

      if (end < eventStart || end > eventEnd) {
        setError('End date must be within the event date range');
        return;
      }
    }

    setError('');
    onSubmit({ startDate, endDate });
  };

  // Calculate year range for date selects based on event dates
  const eventYear = getYearFromDate(event?.start_date);
  const startYear = eventYear;
  const endYear = eventYear + 1;

  // Modal style for dark/light mode
  const modalStyle = {
    backgroundColor: isDark ? '#1e293b' : '#ffffff',
    border: isDark ? '1px solid #334155' : 'none',
  };

  // Custom themed header
  const customHeader = (
    <ModalHeader>
      <ModalHeaderTitle>Add Ticket from Template</ModalHeaderTitle>
      <CloseButton onClick={onClose}>
        <CloseIcon />
      </CloseButton>
    </ModalHeader>
  );

  return (
    <Modal
      isVisible={isVisible}
      hideModal={onClose}
      maxWidth={500}
      modalStyle={modalStyle}
      header={customHeader}
    >
      <ModalContent>
        {template && (
          <>
            <TemplateName>{template.name}</TemplateName>
            <TemplatePrice>{formatPrice(template.price)}</TemplatePrice>
          </>
        )}

        <ModalDescription>
          Select the start and end dates for this ticket. The dates must be
          within the event date range.
        </ModalDescription>

        <FormRowGrid>
          <DateSelect
            label="Ticket Start Date"
            required
            value={startDate}
            onChange={setStartDate}
            startYear={startYear}
            endYear={endYear}
          />
          <DateSelect
            label="Ticket End Date"
            required
            value={endDate}
            onChange={setEndDate}
            startYear={startYear}
            endYear={endYear}
          />
        </FormRowGrid>

        {error && <ErrorText>{error}</ErrorText>}

        <ButtonRow>
          <ButtonWrapper>
            <ButtonV2 type="button" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </ButtonV2>
          </ButtonWrapper>
          <ButtonWrapper>
            <ButtonV2
              elevated
              type="button"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Adding...' : 'Add Ticket'}
            </ButtonV2>
          </ButtonWrapper>
        </ButtonRow>
      </ModalContent>
    </Modal>
  );
};

// Main component wrapped with theme provider
const DateSelectionModal = (props) => {
  return (
    <EnhancedThemeProvider>
      <ModalContentInner {...props} />
    </EnhancedThemeProvider>
  );
};

export default DateSelectionModal;
